import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../services/firebase";
import { PLANES_CONFIG, currentPlan } from "./plans";

// Mismo criterio que functions/limites.js: los valores salen de src/config/planes.json.
// El servidor es el que decide (crearOrden, enviarAMail, prepararDescarga); esto es para avisar antes de subir.

const AR_MS = 3 * 60 * 60 * 1000;
const MB = 1024 * 1024;
const GB = 1024 * MB;

const dayKey = (ms) => new Date(ms - AR_MS).toISOString().slice(0, 10);
const monthKey = (ms) => new Date(ms - AR_MS).toISOString().slice(0, 7);

export const planLimits = (plan) => PLANES_CONFIG.planes[plan] || PLANES_CONFIG.planes.gratis;
export const bytesPerSend = (plan) => planLimits(plan).mbPorEnvio * MB;
export const bytesPerMonth = (plan) => (planLimits(plan).gbPorMes == null ? null : planLimits(plan).gbPorMes * GB);

export function formatLimitBytes(bytes) {
  return bytes >= GB ? `${(bytes / GB).toLocaleString("es-AR", { maximumFractionDigits: 1 })} GB` : `${Math.ceil(bytes / MB).toLocaleString("es-AR")} MB`;
}

// uso/{uid}: los contadores del día y del mes valen 0 si son de otro día u otro mes.
export function currentUsage(uso = {}, now = Date.now()) {
  const delMes = uso?.mes === monthKey(now);
  return {
    enviosDia: uso?.dia === dayKey(now) ? uso.enviosDia || 0 : 0,
    enviosMes: delMes ? uso.enviosMes || 0 : 0,
    bytesMes: delMes ? uso.bytesMes || 0 : 0,
    recibidosMes: delMes ? uso.recibidosMes || 0 : 0,
  };
}

// Error de límite: lleva `limite` (enviosMes, mbPorEnvio, gbPorMes, descargasPorArchivo) para mostrar "Ver planes".
export class LimitError extends Error {
  constructor(limite, message) {
    super(message);
    this.code = `limite/${limite}`;
    this.limite = limite;
  }
}

// El límite que trae un error de la app o de las Cloud Functions (error.details.limite), o null.
export function limitOf(error) {
  return error?.limite || error?.details?.limite || null;
}

export function checkSend({ plan, uso, bytes, now = Date.now() }) {
  const limits = planLimits(plan);
  const actual = currentUsage(uso, now);
  if (limits.enviosPorMes != null && actual.enviosMes >= limits.enviosPorMes) {
    return new LimitError(
      "enviosMes",
      `Tu plan ${limits.nombre} permite ${limits.enviosPorMes} envíos por mes y ya los usaste. Se renuevan el 1° del mes que viene, o podés pasarte a un plan pago.`
    );
  }
  if (bytes > bytesPerSend(plan)) {
    return new LimitError(
      "mbPorEnvio",
      `Tu plan ${limits.nombre} permite ${formatLimitBytes(bytesPerSend(plan))} por envío y este ocupa ${formatLimitBytes(bytes)} (ya comprimido).`
    );
  }
  const perMonth = bytesPerMonth(plan);
  if (perMonth != null && actual.bytesMes + bytes > perMonth) {
    return new LimitError(
      "gbPorMes",
      `Tu plan ${limits.nombre} permite ${formatLimitBytes(perMonth)} de envíos por mes. Llevás ${formatLimitBytes(actual.bytesMes)} y este envío ocupa ${formatLimitBytes(bytes)}.`
    );
  }
  return null;
}

// Control previo con el plan y el uso propios (los dos los puede leer el usuario). Lanza LimitError.
// Si no se pueden leer, no frena el envío: igual lo valida el servidor.
export async function assertCanSend(uid, bytes) {
  let error = null;
  try {
    const [usuario, uso] = await Promise.all([getDoc(doc(db, "usuarios", uid)), getDoc(doc(db, "uso", uid))]);
    error = checkSend({ plan: currentPlan(usuario.data() || {}).plan, uso: uso.data(), bytes });
  } catch (readError) {
    console.warn("[OdontoSend] No se pudo leer el uso del plan; lo valida el servidor.", readError);
  }
  if (error) throw error;
}

export function subscribeToUsage(uid, onData, onError) {
  return onSnapshot(doc(db, "uso", uid), (snapshot) => onData(snapshot.data() || {}), onError);
}
